/**
 * Equipment Generator Data Tables
 * "Mad Libs" style data for generating merchant-like equipment collections
 */

// Item pools organized by category and subcategory
export const itemPools = {
    weapons: {
        swords: ["longsword", "shortsword", "greatsword", "rapier", "scimitar", "falchion", "bastard sword", "cutlass", "saber"],
        axes: ["battleaxe", "handaxe", "greataxe", "throwing axe", "woodcutter's axe", "war pick"],
        blunt: ["mace", "morningstar", "warhammer", "flail", "club", "quarterstaff", "maul"],
        polearms: ["spear", "halberd", "glaive", "pike", "trident", "javelin", "lance", "partisan"],
        daggers: ["dagger", "dirk", "stiletto", "kukri", "kris", "throwing knife", "punch dagger", "boot knife"],
        ranged: ["shortbow", "longbow", "light crossbow", "heavy crossbow", "sling", "hand crossbow", "composite bow"],
        exotic: ["whip", "net", "blowgun", "bolas", "chakram", "spiked chain", "war fan"]
    },

    armor: {
        light: ["leather armor", "studded leather", "padded armor", "hide armor", "leather cuirass"],
        medium: ["chain shirt", "scale mail", "breastplate", "half plate", "brigandine"],
        heavy: ["ring mail", "chain mail", "splint armor", "plate armor", "full plate"],
        shields: ["buckler", "light shield", "heavy shield", "tower shield", "kite shield", "round shield"],
        parts: ["helm", "gauntlets", "greaves", "pauldrons", "vambraces", "gorget", "coif"]
    },

    gear: {
        exploration: ["rope (50 ft)", "grappling hook", "pitons", "hammer", "crowbar", "pole (10 ft)", "iron spikes"],
        camping: ["bedroll", "tent", "blanket", "mess kit", "tinderbox", "torch", "flint and steel"],
        containers: ["backpack", "belt pouch", "sack", "small chest", "barrel", "waterskin", "saddlebags"],
        lighting: ["lantern", "hooded lantern", "bullseye lantern", "candles", "lamp oil", "everburning torch"],
        navigation: ["compass", "map case", "spyglass", "signal whistle", "chalk", "mirror"]
    },

    consumables: {
        food: ["rations (1 day)", "dried meat", "hardtack", "cheese wheel", "wine bottle", "ale flask", "trail bread", "salted fish"],
        potions: ["healing potion", "antitoxin", "potion of climbing", "potion of water breathing", "potion of darkvision"],
        alchemy: ["acid vial", "alchemist's fire", "holy water", "oil flask", "caltrops", "ball bearings", "smoke bomb", "thunderstone"],
        herbs: ["medicinal herbs", "pipe weed", "incense", "dried mushrooms", "healing salve", "fever remedy"]
    },

    tools: {
        artisan: ["smith's tools", "carpenter's tools", "leatherworker's tools", "jeweler's tools", "tinker's tools", "cobbler's tools"],
        specialist: ["thieves' tools", "healer's kit", "herbalism kit", "poisoner's kit", "disguise kit", "forgery kit", "navigator's tools"],
        musical: ["lute", "flute", "drum", "lyre", "horn", "bagpipes", "fiddle", "pan pipes"],
        gaming: ["dice set", "playing cards", "dragonchess set", "three-dragon ante", "knucklebones"]
    },

    magic: {
        focuses: ["wand", "staff", "orb", "crystal", "rod", "amulet", "talisman"],
        components: ["component pouch", "spell components", "arcane focus", "druidic focus", "holy symbol"],
        scrolls: ["spell scroll", "map scroll", "deed scroll", "letter of credit", "proclamation"],
        books: ["spellbook", "tome", "grimoire", "journal", "codex", "bestiary", "atlas"]
    },

    clothing: {
        common: ["cloak", "robe", "tunic", "boots", "gloves", "hat", "belt", "scarf"],
        fine: ["noble's outfit", "courtier's clothes", "scholar's robes", "merchant's garb", "silk shirt"],
        specialty: ["traveler's clothes", "cold weather gear", "desert garb", "sailor's outfit", "entertainer's costume"]
    },

    jewelry: {
        items: ["ring", "necklace", "bracelet", "brooch", "earrings", "circlet", "medallion", "pendant", "signet ring"]
    },

    mounts: {
        tack: ["saddle", "saddlebags", "bit and bridle", "horseshoes", "feed bag", "riding crop"],
        barding: ["leather barding", "chain barding", "plate barding"]
    },

    miscellaneous: {
        trinkets: ["lucky charm", "curious figurine", "old coin", "carved bone", "polished stone", "feather token"],
        documents: ["map", "letter", "contract", "wanted poster", "trade license", "travel papers"],
        religious: ["holy symbol", "prayer beads", "censer", "reliquary", "devotional candle", "icon"]
    }
};

// Descriptors organized by type
export const descriptors = {
    condition: [
        "pristine", "well-maintained", "serviceable", "worn", "battered",
        "weathered", "battle-scarred", "rusted", "tarnished", "cracked",
        "well-worn", "slightly damaged", "repaired", "patched"
    ],

    craftsmanship: [
        "crude", "simple", "sturdy", "well-crafted", "fine",
        "masterwork", "exquisite", "ornate", "intricate", "elegant",
        "practical", "utilitarian", "refined"
    ],

    origin: [
        "elven", "dwarven", "orcish", "gnomish", "halfling-made",
        "human-crafted", "foreign", "exotic", "local", "imported",
        "northern", "southern", "eastern", "western", "ancient"
    ],

    material: [
        "iron", "steel", "bronze", "brass", "copper", "silver-chased",
        "gold-trimmed", "bone", "horn", "leather", "hardwood", "darkwood",
        "oak", "ash", "yew", "ebony", "ivory-inlaid"
    ],

    age: [
        "ancient", "antique", "old", "vintage", "aged",
        "recent", "new", "freshly made", "centuries-old", "time-worn"
    ],

    decoration: [
        "plain", "unadorned", "engraved", "etched", "embossed",
        "jeweled", "gilded", "painted", "carved", "inlaid",
        "rune-marked", "symbol-etched", "filigree"
    ],

    history: [
        "military surplus", "salvaged", "inherited", "looted",
        "commissioned", "found", "won in a duel", "war-trophy",
        "family heirloom", "merchant's stock"
    ]
};

// Themed collections for generating logical groupings
export const themedCollections = [
    {
        name: "Armed Traveler",
        source: "traveling sellsword",
        items: [
            { pool: "weapons.swords" },
            { pool: "armor.light" },
            { pool: "gear.camping" },
            { pool: "consumables.food" }
        ]
    },
    {
        name: "Alchemist's Stock",
        source: "wandering apothecary",
        items: [
            { pool: "consumables.alchemy" },
            { pool: "consumables.alchemy" },
            { pool: "consumables.herbs" },
            { pool: "tools.specialist", filter: ["herbalism kit", "poisoner's kit"] }
        ]
    },
    {
        name: "Thief's Kit",
        source: "shadowy fence",
        items: [
            { pool: "weapons.daggers" },
            { pool: "tools.specialist", filter: ["thieves' tools", "disguise kit"] },
            { pool: "clothing.common", filter: ["cloak"] },
            { pool: "gear.exploration", filter: ["rope (50 ft)", "grappling hook"] }
        ]
    },
    {
        name: "Wizard's Supplies",
        source: "hedge wizard's estate",
        items: [
            { pool: "magic.focuses" },
            { pool: "magic.components" },
            { pool: "magic.books" },
            { pool: "consumables.alchemy" }
        ]
    },
    {
        name: "Soldier's Kit",
        source: "military quartermaster",
        items: [
            { pool: "weapons.swords" },
            { pool: "armor.medium" },
            { pool: "armor.shields" },
            { pool: "consumables.food" }
        ]
    },
    {
        name: "Healer's Bag",
        source: "village healer",
        items: [
            { pool: "tools.specialist", filter: ["healer's kit", "herbalism kit"] },
            { pool: "consumables.potions" },
            { pool: "consumables.potions" },
            { pool: "consumables.herbs" }
        ]
    },
    {
        name: "Explorer's Pack",
        source: "retired adventurer",
        items: [
            { pool: "gear.exploration" },
            { pool: "gear.exploration" },
            { pool: "gear.lighting" },
            { pool: "gear.navigation" }
        ]
    },
    {
        name: "Merchant's Wares",
        source: "traveling merchant",
        items: [
            { pool: "clothing.fine" },
            { pool: "jewelry.items" },
            { pool: "tools.gaming" },
            { pool: "consumables.food", filter: ["wine bottle", "ale flask"] }
        ]
    },
    {
        name: "Bard's Collection",
        source: "wandering minstrel",
        items: [
            { pool: "tools.musical" },
            { pool: "magic.books", filter: ["journal", "codex"] },
            { pool: "clothing.fine" },
            { pool: "consumables.food", filter: ["wine bottle", "ale flask"] }
        ]
    },
    {
        name: "Ranger's Gear",
        source: "forest guide",
        items: [
            { pool: "weapons.ranged" },
            { pool: "weapons.daggers" },
            { pool: "gear.camping" },
            { pool: "consumables.food" }
        ]
    },
    {
        name: "Knight's Equipment",
        source: "noble's armory",
        items: [
            { pool: "weapons.swords" },
            { pool: "armor.heavy" },
            { pool: "armor.shields" },
            { pool: "mounts.tack" }
        ]
    },
    {
        name: "Cleric's Tools",
        source: "temple storeroom",
        items: [
            { pool: "miscellaneous.religious" },
            { pool: "consumables.potions", filter: ["healing potion"] },
            { pool: "magic.books" },
            { pool: "clothing.common", filter: ["robe"] }
        ]
    },
    {
        name: "Sailor's Belongings",
        source: "shipwreck salvage",
        items: [
            { pool: "weapons.daggers", filter: ["dagger", "dirk", "cutlass"] },
            { pool: "gear.navigation" },
            { pool: "gear.exploration", filter: ["rope (50 ft)"] },
            { pool: "consumables.food", filter: ["salted fish", "ale flask"] }
        ]
    },
    {
        name: "Craftsman's Tools",
        source: "retired artisan",
        items: [
            { pool: "tools.artisan" },
            { pool: "tools.artisan" },
            { pool: "gear.containers" },
            { pool: "clothing.common" }
        ]
    },
    {
        name: "Dungeon Delver's Kit",
        source: "tomb raider's cache",
        items: [
            { pool: "gear.exploration" },
            { pool: "gear.lighting" },
            { pool: "consumables.alchemy" },
            { pool: "weapons.daggers" }
        ]
    },
    {
        name: "Hunter's Pack",
        source: "trapper's stash",
        items: [
            { pool: "weapons.ranged" },
            { pool: "weapons.daggers" },
            { pool: "gear.camping" },
            { pool: "gear.containers", filter: ["sack", "belt pouch"] }
        ]
    },
    {
        name: "Noble's Effects",
        source: "estate sale",
        items: [
            { pool: "jewelry.items" },
            { pool: "clothing.fine" },
            { pool: "miscellaneous.documents" },
            { pool: "consumables.food", filter: ["wine bottle"] }
        ]
    },
    {
        name: "Mercenary's Arsenal",
        source: "sellsword company",
        items: [
            { pool: "weapons.axes" },
            { pool: "weapons.daggers" },
            { pool: "armor.light" },
            { pool: "armor.parts" }
        ]
    },
    {
        name: "Traveling Scholar",
        source: "university sale",
        items: [
            { pool: "magic.books" },
            { pool: "magic.scrolls" },
            { pool: "gear.containers", filter: ["backpack", "map case"] },
            { pool: "gear.lighting", filter: ["candles", "lantern"] }
        ]
    },
    {
        name: "Cavalry Supplies",
        source: "horse trader",
        items: [
            { pool: "mounts.tack" },
            { pool: "mounts.tack" },
            { pool: "weapons.polearms", filter: ["lance", "spear"] },
            { pool: "armor.parts" }
        ]
    }
];

// Accessories that can be paired with items
export const accessories = {
    swords: ["leather scabbard", "ornate sheath", "baldric", "sword belt", "maintenance kit", "oiled cloth"],
    axes: ["leather loop", "belt frog", "oiled cloth", "whetstone", "carrying strap"],
    daggers: ["belt sheath", "boot holster", "arm sheath", "leather wrap"],
    ranged: ["quiver of arrows", "bolt case", "spare bow string", "arm guard", "finger tab"],
    armor: ["spare padding", "repair straps", "polishing cloth", "carrying stand", "maintenance oil"],
    potions: ["carrying case", "belt loops", "padded pouch", "labeled stoppers"],
    books: ["leather cover", "bookmark", "reading glasses", "candle", "ink and quill"],
    tools: ["tool roll", "carrying case", "maintenance supplies", "spare parts"],
    jewelry: ["velvet pouch", "wooden box", "protective wrap"],
    clothing: ["matching accessories", "spare buttons", "cleaning brush"]
};

// Sources for random generation
export const sources = [
    "traveling peddler",
    "village merchant",
    "retired adventurer",
    "military quartermaster",
    "estate sale",
    "shipwreck salvage",
    "dungeon hoard",
    "thieves' fence",
    "wandering trader",
    "caravan remnants",
    "abandoned camp",
    "monastery dissolution",
    "guild auction",
    "war memorial",
    "treasure hoard"
];
